// src/pages/calculator/index.jsx — Verduurzaamwijzer besparingscalculator (wizard in 3 stappen)
// Rekenlogica: src/lib/calculatorEngine.js · Stap 3: ./Advies.jsx · Bouwstenen: ./ui.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { calc, afrondenAntwoorden, initialState, VERBRUIK_OPT, PANELEN_OPT, TERUG, eur, eurc, rng, kwh, pct, naarKosten } from '../../lib/calculatorEngine';
import { track, Eyebrow, Card, Note, Btn, Bar, scrollNaarBoven } from './ui';
import Advies from './Advies';

// ---------- Vragen ----------
const QUESTIONS = [
  { key: 'verbruik', title: 'Hoeveel stroom verbruik je per jaar?', lead: 'Je hoeft niets op te zoeken; een ruwe schatting is genoeg. Weet je het precies, dan kun je het hieronder invullen.',
    opts: [['laag', 'Laag', '± 2.300 kWh · 1–2 personen'], ['gemiddeld', 'Gemiddeld', '± 3.500 kWh · een doorsnee gezin'], ['hoog', 'Hoog', '± 4.500 kWh · groot gezin of veel apparaten'], ['veel', 'Veel', '± 6.500 kWh · warmtepomp en/of elektrische auto']],
    exact: { label: 'Ik weet mijn exacte jaarverbruik', unit: 'kWh', min: 500, max: 30000, step: 50 } },
  { key: 'panelen', title: 'Hoeveel zonnepanelen heb je nu?', lead: 'Ook zonder panelen rekenen we door wat zonnepanelen en een batterij voor je doen.',
    opts: [['geen', 'Geen panelen', 'nog geen zonnepanelen'], ['tot8', 'Tot 8 panelen', '± 2.300 kWh per jaar'], ['p9_14', '9 tot 14 panelen', '± 4.300 kWh per jaar'], ['p15', '15 of meer', '± 7.000 kWh per jaar']],
    exact: { label: 'Ik weet het exacte aantal', unit: 'panelen', min: 0, max: 60, step: 1 } },
  { key: 'moment', title: 'Wanneer gebruik je thuis de meeste stroom?', lead: 'Dit bepaalt hoeveel zonnestroom je direct zelf kunt gebruiken en hoeveel er in een batterij zou passen.',
    opts: [['avond', "Vooral \'s avonds", 'overdag meestal weg; wassen, koken en opladen na vijven'], ['verspreid', 'Verspreid over de dag', 'soms thuis, soms weg'], ['overdag', 'Vooral overdag', 'vaak thuis; je gebruikt al veel direct']] },
  { key: 'extras', title: 'Wat heb je in huis, of komt er binnen twee jaar?', lead: 'Vink aan wat van toepassing is. Niets? Ga dan gewoon verder.', multi: true },
  { key: 'contract', title: 'Wat voor energiecontract heb je?', lead: 'Met een dynamisch contract betaal je de uurprijs. Een batterij kan dan laden als stroom goedkoop is, ook in de winter.',
    opts: [['vast', 'Vast of variabel', 'een vast tarief of een tarief dat per maand of half jaar wijzigt'], ['dynamisch', 'Dynamisch', 'uurprijzen, bijvoorbeeld bij Tibber, Frank, ANWB of Zonneplan'], ['weetniet', 'Weet ik niet', 'geen probleem, we rekenen voorzichtig']] },
];

// ---------- Stap 1: vragen ----------
function Questions({ state, setState, onDone }) {
  const [i, setI] = useState(0);
  const [exact, setExact] = useState('');
  const q = QUESTIONS?.[i];
  useEffect(() => { scrollNaarBoven(); track('calc_stap', { stap: i + 1 }); }, [i]);

  const next = (patch) => {
    const s = { ...state, ...patch };
    setState(s);
    if (i < QUESTIONS?.length - 1) { setI(i + 1); setExact(''); } else onDone(s);
  };
  const choose = (v) => {
    if (q?.key === 'verbruik') next({ verbruikKeuze: v, verbruik: VERBRUIK_OPT?.[v] });
    else if (q?.key === 'panelen') next({ panelenKeuze: v, panelen: PANELEN_OPT?.[v] });
    else next({ [q?.key]: v });
  };
  const chooseExact = () => {
    const v = +exact; if (!isFinite(v) || exact === '') return;
    if (q?.key === 'verbruik') next({ verbruikKeuze: 'exact', verbruik: Math.max(500, v) });
    if (q?.key === 'panelen') next({ panelenKeuze: 'exact', panelen: Math.max(0, Math.round(v)) });
  };
  const cur = state?.[q?.key + 'Keuze'] ?? state?.[q?.key];

  return (
    <div className="mx-auto max-w-2xl pt-4">
      <Eyebrow>Vraag {i + 1} van {QUESTIONS?.length}</Eyebrow>
      <h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">{q?.title}</h1>
      <p className="mt-2.5 max-w-prose text-stone-500">{q?.lead}</p>

      {q?.multi ? (
        <>
          <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
            {[['warmtepomp', 'Warmtepomp', "Verbruikt vooral 's avonds en in de winter"], ['ev', 'Elektrische auto', 'Laadt (deels) thuis']]?.map(([k, t, sub]) => (
              <label key={k} className={`flex cursor-pointer items-start gap-3 rounded-2xl border-2 p-4 ${state?.[k] ? 'border-emerald-700 bg-emerald-50' : 'border-stone-200 bg-white'}`}>
                <input type="checkbox" className="mt-0.5 h-5 w-5 accent-emerald-700" checked={!!state?.[k]} onChange={(e) => setState({ ...state, [k]: e?.target?.checked })} />
                <span><b className="block font-semibold">{t}</b><small className="block text-sm text-stone-500">{sub}</small></span>
              </label>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <button type="button" className="text-stone-500 underline underline-offset-4" onClick={() => setI(i - 1)}>← Vorige</button>
            <Btn onClick={() => next({})}>Verder →</Btn>
          </div>
        </>
      ) : (
        <>
          <div className={`mt-5 grid gap-2.5 ${q?.opts?.length > 3 ? 'sm:grid-cols-2' : ''}`}>
            {q?.opts?.map(([v, t, sub]) => (
              <button key={v} type="button" onClick={() => choose(v)} aria-pressed={cur === v}
                className={`rounded-2xl border-2 p-4 text-left transition hover:border-emerald-700 ${cur === v ? 'border-emerald-700 bg-emerald-50' : 'border-stone-200 bg-white'}`}>
                <b className="block text-lg font-bold">{t}</b><span className="block text-sm text-stone-500">{sub}</span>
              </button>
            ))}
          </div>
          {q?.exact && (
            <details className="mt-3.5">
              <summary className="cursor-pointer text-sm font-semibold text-emerald-700">+ {q?.exact?.label}</summary>
              <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                <div className="relative w-44">
                  <input type="number" inputMode="numeric" min={q?.exact?.min} max={q?.exact?.max} step={q?.exact?.step} value={exact} onChange={(e) => setExact(e?.target?.value)}
                    onKeyDown={(e) => { if (e?.key === 'Enter') { e?.preventDefault(); chooseExact(); } }}
                    className="w-full rounded-xl border border-stone-300 bg-white py-2.5 pl-3 pr-14 tabular-nums" />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-stone-500">{q?.exact?.unit}</span>
                </div>
                <Btn sm onClick={chooseExact}>Verder →</Btn>
              </div>
            </details>
          )}
          <div className="mt-5 flex items-center justify-between">
            {i > 0 ? <button type="button" className="text-stone-500 underline underline-offset-4" onClick={() => setI(i - 1)}>← Vorige</button> : <span />}
            {i === 0 && <Note>± 2 minuten · 5 vragen</Note>}
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Overgang ----------
function Calculating({ onDone }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const d = window.matchMedia('(prefers-reduced-motion: reduce)')?.matches ? 0 : 420;
    const ts = [1, 2, 3]?.map((k) => setTimeout(() => setN(k), d * k));
    const done = setTimeout(onDone, d * 3 + 250);
    return () => { ts?.forEach(clearTimeout); clearTimeout(done); };
  }, [onDone]);
  const steps = ['Verbruik en opwek vergelijken', 'Einde saldering per 2027 doorrekenen', '14 thuisbatterijen uit de gids doorrekenen'];
  return (
    <div className="mx-auto max-w-2xl py-16 text-center">
      <Eyebrow>Even geduld</Eyebrow>
      <h2 className="mt-1 text-2xl font-bold">We rekenen jouw situatie door</h2>
      <ol className="mx-auto mt-5 grid max-w-xs gap-2 text-left">
        {steps?.map((s, k) => (
          <li key={s} className={`flex items-center gap-2.5 ${n > k ? 'text-stone-900' : 'text-stone-500'}`}>
            <span className={`h-4 w-4 rounded-full border-2 ${n > k ? 'border-emerald-700 bg-emerald-700' : 'border-stone-300'}`} />{s}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---------- Stap 2: inzicht ----------
function Inzicht({ state: s, onBack, onNext }) {
  const r = useMemo(() => calc(s), [s]);
  useEffect(() => { scrollNaarBoven(); track('calc_inzicht', { stijging_2027: Math.round(r?.stijging) }); }, []);
  const heeftPanelen = s?.panelen > 0;
  const batTxt = r?.bat?.aan ? `${r?.bat?.chosen?.merk} ${r?.bat?.chosen?.model} van ${r?.bat?.cap} kWh` : 'thuisbatterij';
  const dek = r?.stijging > 0 ? Math.min(1.5, r?.bat?.besp / r?.stijging) : 0;
  const m = r?.met;
  const steps = [
    ['Je panelen wekken op', kwh(m?.opb), `${heeftPanelen ? s?.panelen + (r?.zon?.aan ? ' + ' + s?.panelenNieuw + ' nieuwe' : '') : s?.panelenNieuw + ' nieuwe'} panelen, circa ${Math.round(m?.kwp * 10) / 10} kWp`],
    ['Daarvan gebruik je direct', kwh(m?.direct), `${pct(m?.share)}: ${s?.moment === 'avond' ? 'overdag ben je vaak weg' : s?.moment === 'overdag' ? 'je bent vaak thuis' : 'soms thuis, soms weg'}${s?.warmtepomp ? ', de warmtepomp draait overdag mee' : ''}`],
    ['Het overschot gaat naar het net', kwh(m?.overschot), `${heeftPanelen ? 'nu nog gesaldeerd; ' : ''}vanaf 2027 tegen circa ${eurc(TERUG)} per kWh`],
    ...(r?.bat?.aan ? [[`Een batterij van ${r?.bat?.cap} kWh vangt daarvan op`, kwh(m?.verschoven), `${kwh(m?.nuttig)} bruikbaar na laadverlies; niet elke zomerpiek past erin en in de winter is er weinig te vangen`]] : []),
    ['Je koopt nog in van het net', kwh(m?.ingekocht), `tegen ${eurc(r?.prijs)} per kWh${r?.dyn?.aan || r?.dynNow ? '; met een dynamisch contract laadt de batterij ook bij lage uurprijzen' : ''}`],
  ];
  return (
    <div className="mx-auto max-w-2xl">
      <div className="pt-4">
        <Eyebrow>Stap 2 · Wat verandert er voor jou</Eyebrow>
        <h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">{heeftPanelen ? 'Dit verandert er voor jou in 2027' : 'Zo staat je stroomrekening er nu voor'}</h1>
        <p className="mt-2.5 text-stone-500">{heeftPanelen
          ? `Je ${s?.panelen} panelen wekken circa ${kwh(r?.basis?.opb)} per jaar op. Daarvan gebruik je ${pct(r?.basis?.share)} direct; de rest gaat nu nog gesaldeerd het net op.`
          : `Zonder eigen opwek koop je al je ${kwh(s?.verbruik)} in. Het einde van de saldering raakt jou niet direct, maar elke stijging van de stroomprijs wel.`}</p>
      </div>
      <div className="mt-4 grid gap-3">
        <Card deep>
          <Eyebrow className="!text-emerald-100/70">{heeftPanelen ? 'Vanaf 2027 stijgt jouw stroomrekening met' : 'Je betaalt nu per jaar circa'}</Eyebrow>
          <div className="my-1 text-5xl font-bold tracking-tight tabular-nums md:text-6xl">± {eur(heeftPanelen ? Math.max(0, r?.stijging) : r?.kostenZonderPanelen)}</div>
          <div className="text-lg opacity-80">{heeftPanelen ? 'per jaar' : 'aan stroom'}</div>
          <p className="mt-2.5 max-w-prose text-sm opacity-85">{heeftPanelen
            ? `Omdat salderen stopt, levert de ${kwh(r?.basis?.salderbaar)} die je nu wegstreept nog maar circa ${eurc(TERUG)} per kWh op, terwijl je 's avonds ${eurc(r?.prijs)} betaalt om stroom terug te kopen.`
            : `Met ${s?.panelenNieuw} zonnepanelen${r?.bat?.aan ? ' en een ' + batTxt : ''} daalt dat naar ${naarKosten(r?.kosten2027Plan)}, gerekend met de situatie na 2027 (zonder saldering).`}</p>
        </Card>
        <Card>
          <Eyebrow>Wat één kWh zonnestroom waard is vanaf 2027</Eyebrow>
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            <div className="rounded-xl border border-stone-200 p-3"><div className="text-xl font-bold">{eurc(r?.prijs)}</div><div className="text-xs text-stone-500">zelf gebruiken: die kWh hoef je niet te kopen</div></div>
            <div className="rounded-xl border border-stone-200 p-3"><div className="text-xl font-bold">€ 0,08 – 0,13</div><div className="text-xs text-stone-500">terugleveren: minimaal de helft van het kale tarief, tot en met 2030</div></div>
          </div>
          <Note className="mt-2.5">Stroom die je zelf gebruikt is ruwweg drie keer zoveel waard als stroom die je teruglevert. Een batterij helpt je meer zelf te gebruiken.</Note>
        </Card>
        <Card>
          <Eyebrow>{heeftPanelen ? 'Eigen verbruik van je zonnestroom' : `Eigen verbruik van de zonnestroom van ${s?.panelenNieuw} nieuwe panelen`}</Eyebrow>
          <div className="mt-3 grid gap-2.5">
            <Bar label={heeftPanelen ? 'Alleen zonnepanelen' : 'Zonder batterij'} value={heeftPanelen ? r?.basis?.share : r?.stapZon?.share} />
            <Bar label="Met thuisbatterij" value={r?.met?.eigen} sun />
          </div>
          <Note className="mt-2.5">{heeftPanelen
            ? (r?.bat?.aan ? `Met een ${batTxt}${r?.dyn?.aan || r?.dynNow ? ' en slim laden op een dynamisch contract' : ''} bespaar je circa ${eur(r?.bat?.besp)} per jaar: dat ${dek >= 0.95 ? 'dekt de stijging vrijwel helemaal' : dek >= 0.6 ? 'dekt het grootste deel van de stijging' : 'dekt een deel van de stijging'}.` : '')
            : 'Hoe meer zonnestroom je zelf gebruikt, hoe sneller panelen zich terugverdienen; na 2027 is zelf gebruiken ruim drie keer zoveel waard als terugleveren.'}</Note>
        </Card>
        <Card>
          <Eyebrow>Jouw rekensom, stap voor stap</Eyebrow>
          <div className="mt-3">
            {steps?.map(([t, v, sub], k) => (
              <div key={t} className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-start gap-3 border-b border-stone-200 py-3 last:border-b-0">
                <span className="pt-0.5 font-mono text-sm text-stone-500">{String(k + 1)?.padStart(2, '0')}</span>
                <div><b className="block font-semibold">{t}</b><small className="block text-sm text-stone-500">{sub}</small></div>
                <div className="whitespace-nowrap text-right text-lg font-bold tabular-nums">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3.5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-dashed border-stone-300 p-3.5"><Eyebrow>Nu, tot en met 2026</Eyebrow><div className="text-2xl font-bold tabular-nums">{rng(r?.totaal?.bespNu)}</div><Note>per jaar{heeftPanelen ? ': vooral bespaarde terugleverkosten; salderen doet nu nog het meeste werk' : ''}</Note></div>
            <div className="rounded-xl border border-emerald-700 bg-emerald-50 p-3.5"><Eyebrow>Vanaf 2027</Eyebrow><div className="text-2xl font-bold tabular-nums">{rng(r?.totaal?.besp)}</div><Note>per jaar: zelf gebruiken scheelt {eurc(r?.prijs)} per kWh in plaats van terugleveren tegen {eurc(TERUG)}</Note></div>
          </div>
          <Note className="mt-3">Alle bedragen zijn een schatting met een marge van zo'n 15% naar boven en beneden. Op het volgende scherm pas je de stroomprijs, de maat en het merk zelf aan.</Note>
        </Card>
      </div>
      <div className="mt-5 flex items-center justify-between gap-3">
        <button type="button" className="text-stone-500 underline underline-offset-4" onClick={onBack}>← Antwoorden aanpassen</button>
        <Btn onClick={onNext}>Bekijk mijn advies →</Btn>
      </div>
    </div>
  );
}

// ---------- Pagina ----------
export default function CalculatorPage({ embedded = false }) {
  const [state, setState] = useState(initialState);
  const [view, setView] = useState('vragen'); // vragen | berekenen | inzicht | advies
  const progress = { vragen: 1, berekenen: 2, inzicht: 2, advies: 3 }?.[view];

  const onDone = (s) => { setState(afrondenAntwoorden(s)); setView('berekenen'); };

  const Wrapper = embedded ? 'div' : 'main';
  return (
    <Wrapper className={`${embedded ? 'rounded-3xl' : 'min-h-screen'} bg-stone-50 text-stone-900`}>
      <div className={`mx-auto max-w-6xl px-4 ${embedded ? 'pb-8' : 'pb-28'}`}>
        <header className="flex items-center justify-between gap-4 pb-2.5 pt-5">
          <div className="text-base font-bold tracking-tight">Verduurzaamwijzer<small className="block text-xs font-medium uppercase tracking-wider text-stone-500">Bereken je besparing</small></div>
          <div className="flex items-center gap-2.5 whitespace-nowrap text-sm text-stone-500">Stap {progress} van 3<div className="h-1.5 w-20 overflow-hidden rounded-full bg-stone-200 sm:w-32"><i className="block h-full rounded-full bg-emerald-700 transition-all" style={{ width: `${Math.round(progress / 3 * 100)}%` }} /></div></div>
        </header>
        {view === 'vragen' && <Questions state={state} setState={setState} onDone={onDone} />}
        {view === 'berekenen' && <Calculating onDone={() => setView('inzicht')} />}
        {view === 'inzicht' && <Inzicht state={state} onBack={() => setView('vragen')} onNext={() => setView('advies')} />}
        {view === 'advies' && <Advies state={state} setState={setState} onBack={() => setView('inzicht')} />}
      </div>
    </Wrapper>
  );
}
